import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, TextField, Button,
  Alert, CircularProgress, Stack, Divider, Chip,
  Dialog, DialogContent,
  Autocomplete, InputAdornment, LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Payment, CheckCircle, AccountBalanceWallet, Print,
  Search, Warning, ArrowDownward,
} from '@mui/icons-material';
import api from '../../services/api';

interface Servico {
  id: string;
  nome: string;
  codigo: string;
  grupo: string;
}

interface WalletInfo {
  saldo: number;
  saldoMinimo: number;
  estado: string;
}

interface Recibo {
  transacaoId: string;
  entidadeCodigo: string;
  entidadeNome: string;
  referencia: string;
  montante: number;
  comissao: number;
  montanteCobrado: number;
  saldoAnterior: number;
  saldoNovo: number;
  estado: string;
  dtTransacao: string;
  agente: { nome: string; codigo: string };
}

const fmt = (n: number) =>
  new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE', maximumFractionDigits: 0 }).format(n);

const fmtDt = (dt: string) =>
  new Date(dt).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** Format number string with thousands separators while typing */
const formatDisplayAmount = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  return new Intl.NumberFormat('pt-PT').format(Number(digits));
};

const ENTITY_COLORS: string[] = [
  '#1565C0', '#2E7D32', '#6A1B9A', '#C62828', '#E65100',
  '#00695C', '#283593', '#AD1457',
];

const getEntityColor = (code: string): string => {
  let hash = 0;
  for (let i = 0; i < code.length; i++) hash = code.charCodeAt(i) + ((hash << 5) - hash);
  return ENTITY_COLORS[Math.abs(hash) % ENTITY_COLORS.length];
};

export default function AgentePagar() {
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [contrato, setContrato] = useState<any>(null);
  const [selectedServico, setSelectedServico] = useState<Servico | null>(null);
  const [referencia, setReferencia] = useState('');
  const [montante, setMontante] = useState('');
  const [displayMontante, setDisplayMontante] = useState('');
  const [loading, setLoading] = useState(false);
  const [recibo, setRecibo] = useState<Recibo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    Promise.allSettled([
      api.get('/agente/servicos'),
      api.get('/agente/wallet'),
      api.get('/agente/contrato'),
    ]).then(([sRes, wRes, cRes]) => {
      if (sRes.status === 'fulfilled') setServicos(sRes.value.data?.data?.servicos || []);
      if (wRes.status === 'fulfilled') setWallet(wRes.value.data?.data?.wallet || null);
      if (cRes.status === 'fulfilled') setContrato(cRes.value.data?.data || null);
    });
  }, []);

  const montanteNum = parseFloat(montante) || 0;
  const comissao = contrato
    ? contrato.TIPO_COMISSAO === 'FIXA'
      ? Number(contrato.VALOR_COMISSAO)
      : montanteNum * (Number(contrato.VALOR_COMISSAO) / 100)
    : 0;
  const total = montanteNum + comissao;
  const saldoInsuficiente = wallet ? wallet.saldo < montanteNum : false;
  const saldoDepois = wallet ? wallet.saldo - montanteNum + comissao : 0;
  const walletPercent = wallet && wallet.saldoMinimo > 0
    ? Math.min(100, (wallet.saldo / (wallet.saldoMinimo * 3)) * 100)
    : 100;

  const handleMontanteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '');
    setMontante(raw);
    setDisplayMontante(formatDisplayAmount(raw));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.post('/agente/pagar', {
        entidadeCodigo: selectedServico?.codigo,
        entidadeNome: selectedServico?.nome,
        referencia,
        montante: montanteNum,
      });
      setRecibo(res.data?.data);
      if (res.data?.data?.saldoNovo !== undefined) {
        setWallet(w => w ? { ...w, saldo: res.data.data.saldoNovo } : w);
      }
      setSelectedServico(null);
      setReferencia('');
      setMontante('');
      setDisplayMontante('');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erro ao processar pagamento');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const canSubmit = selectedServico && referencia && montanteNum > 0 && !loading && !saldoInsuficiente;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      {/* Page header */}
      <Box mb={3}>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
          <Box sx={{ p: 1, bgcolor: 'primary.50', borderRadius: 1.5, display: 'flex' }}>
            <Payment color="primary" sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={800} lineHeight={1}>
              Processar Pagamento
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Seleccione o serviço e insira os dados da factura do cliente
            </Typography>
          </Box>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Wallet balance card */}
      {wallet && (
        <Card
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 2.5,
            borderColor: saldoInsuficiente ? 'error.main' : montanteNum > 0 ? 'success.300' : 'primary.200',
            bgcolor: saldoInsuficiente ? 'error.50' : 'background.paper',
          }}
        >
          <CardContent sx={{ p: '16px !important' }}>
            <Stack direction="row" alignItems="flex-start" spacing={2}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: saldoInsuficiente ? 'error.100' : 'primary.100',
                  display: 'flex',
                  flexShrink: 0,
                }}
              >
                {saldoInsuficiente
                  ? <Warning color="error" sx={{ fontSize: 22 }} />
                  : <AccountBalanceWallet color="primary" sx={{ fontSize: 22 }} />
                }
              </Box>
              <Box flex={1}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ letterSpacing: 0.5 }}>
                  SALDO DISPONÍVEL
                </Typography>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  color={saldoInsuficiente ? 'error.main' : 'primary.main'}
                  sx={{ lineHeight: 1.1 }}
                >
                  {fmt(wallet.saldo)}
                </Typography>
                {wallet.saldoMinimo > 0 && (
                  <>
                    <LinearProgress
                      variant="determinate"
                      value={walletPercent}
                      color={saldoInsuficiente ? 'error' : 'primary'}
                      sx={{ height: 5, borderRadius: 4, mt: 0.75, mb: 0.5 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Saldo mínimo: {fmt(wallet.saldoMinimo)}
                    </Typography>
                  </>
                )}
              </Box>
              <Chip
                label={wallet.estado}
                size="small"
                color={wallet.estado === 'ATIVO' ? 'success' : 'error'}
                sx={{ flexShrink: 0, fontWeight: 600 }}
              />
            </Stack>
            {saldoInsuficiente && montanteNum > 0 && (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5, borderRadius: 1.5 }}>
                Saldo insuficiente. Faltam {fmt(montanteNum - wallet.saldo)} para processar este pagamento.
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payment form */}
      <Card variant="outlined" sx={{ borderRadius: 2.5, mb: 2 }}>
        <CardContent sx={{ p: '20px !important' }}>
          {/* Section header */}
          <Typography variant="subtitle2" fontWeight={700} color="text.secondary" mb={2} sx={{ letterSpacing: 0.5 }}>
            DADOS DO PAGAMENTO
          </Typography>

          <Stack spacing={2.5}>
            {/* Service selector */}
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.75}>
                Serviço *
              </Typography>
              <Autocomplete
                options={servicos}
                groupBy={o => o.grupo}
                getOptionLabel={o => o.nome}
                value={selectedServico}
                onChange={(_, v) => setSelectedServico(v)}
                renderInput={params => (
                  <TextField
                    {...params}
                    size="small"
                    placeholder="Pesquisar (ELECTRA, ÁGUAS, CV Telecom…)"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Search fontSize="small" sx={{ color: 'text.disabled', mr: 0.5, ml: -0.5 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Stack direction="row" alignItems="center" spacing={1.5} py={0.25}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: getEntityColor(option.codigo),
                          fontSize: 13,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {option.nome[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{option.nome}</Typography>
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                          {option.codigo}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                )}
                noOptionsText="Nenhum serviço encontrado"
              />
              {selectedServico && (
                <Chip
                  label={selectedServico.codigo}
                  size="small"
                  sx={{
                    mt: 0.75,
                    bgcolor: getEntityColor(selectedServico.codigo),
                    color: 'white',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: 11,
                  }}
                />
              )}
            </Box>

            {/* Reference */}
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.75}>
                Referência da Factura *
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={referencia}
                onChange={e => setReferencia(e.target.value)}
                placeholder="Ex: 123456789"
              />
            </Box>

            {/* Amount */}
            <Box>
              <Typography variant="body2" fontWeight={600} mb={0.75}>
                Montante (CVE) *
              </Typography>
              <TextField
                size="small"
                fullWidth
                value={displayMontante}
                onChange={handleMontanteChange}
                placeholder="0"
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">CVE</InputAdornment>,
                }}
              />
            </Box>
          </Stack>

          {/* Summary box */}
          {contrato && montanteNum > 0 && (
            <>
              <Divider sx={{ my: 2.5 }} />
              <Box
                sx={{
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ letterSpacing: 0.5 }}>
                  RESUMO
                </Typography>

                <Stack spacing={0.75} mt={1.25}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">Valor da factura:</Typography>
                    <Typography variant="body2" fontWeight={500}>{fmt(montanteNum)}</Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Comissão ({contrato.TIPO_COMISSAO === 'FIXA'
                        ? `${fmt(contrato.VALOR_COMISSAO)} fixo`
                        : `${contrato.VALOR_COMISSAO}%`}):
                    </Typography>
                    <Chip
                      label={`+${fmt(comissao)}`}
                      size="small"
                      sx={{
                        bgcolor: 'success.50',
                        color: 'success.main',
                        fontWeight: 700,
                        fontSize: 12,
                      }}
                    />
                  </Stack>

                  <Divider sx={{ my: 0.25 }} />

                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" fontWeight={700}>Total a cobrar ao cliente:</Typography>
                    <Typography variant="h6" fontWeight={800} color="primary.main">
                      {fmt(total)}
                    </Typography>
                  </Stack>

                  {wallet && (
                    <>
                      <Divider sx={{ my: 0.25 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <ArrowDownward sx={{ fontSize: 13, color: 'text.disabled' }} />
                          <Typography variant="caption" color="text.secondary">
                            Saldo após pagamento:
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          color={saldoDepois < 0 ? 'error.main' : saldoDepois < (wallet.saldoMinimo || 0) ? 'warning.main' : 'text.primary'}
                        >
                          {fmt(saldoDepois)}
                        </Typography>
                      </Stack>
                    </>
                  )}
                </Stack>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Submit button */}
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={handleSubmit}
        disabled={!canSubmit}
        startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Payment />}
        color={saldoInsuficiente ? 'error' : 'primary'}
        sx={{
          borderRadius: 2,
          py: 1.5,
          fontSize: 16,
          fontWeight: 700,
          boxShadow: canSubmit ? '0 4px 14px rgba(21,101,192,0.35)' : 'none',
        }}
      >
        {loading
          ? 'A processar…'
          : saldoInsuficiente
          ? 'Saldo insuficiente'
          : total > 0
          ? `Processar — ${fmt(total)}`
          : 'Processar Pagamento'}
      </Button>

      {/* Receipt dialog */}
      <Dialog
        open={!!recibo}
        onClose={() => setRecibo(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        {recibo && (
          <Box ref={printRef}>
            {/* Receipt header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #2E7D32 0%, #1B5E20 100%)',
                color: 'white',
                px: 3,
                py: 2.5,
                textAlign: 'center',
              }}
            >
              <CheckCircle sx={{ fontSize: 40, mb: 0.5 }} />
              <Typography variant="h6" fontWeight={800}>
                Pagamento Processado
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Pagali · Portal do Agente
              </Typography>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {/* Service + reference */}
              <Box sx={{ px: 3, py: 2, textAlign: 'center', bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    bgcolor: getEntityColor(recibo.entidadeCodigo),
                    fontSize: 18,
                    fontWeight: 700,
                    mx: 'auto',
                    mb: 0.75,
                  }}
                >
                  {recibo.entidadeNome[0]}
                </Avatar>
                <Typography variant="body1" fontWeight={700}>{recibo.entidadeNome}</Typography>
                <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                  Referência: {recibo.referencia}
                </Typography>
              </Box>

              {/* Transaction details */}
              <Box sx={{ px: 3, py: 2 }}>
                {[
                  { label: 'Montante factura', value: fmt(recibo.montante) },
                  { label: 'Comissão', value: `+${fmt(recibo.comissao)}`, color: 'success.main' },
                  { label: 'Total cobrado', value: fmt(recibo.montanteCobrado), bold: true, size: 'large' },
                ].map(({ label, value, bold, color, size }) => (
                  <Stack key={label} direction="row" justifyContent="space-between" alignItems="center" py={0.6}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography
                      variant={size === 'large' ? 'body1' : 'body2'}
                      fontWeight={bold ? 800 : 400}
                      color={color || 'text.primary'}
                    >
                      {value}
                    </Typography>
                  </Stack>
                ))}

                <Divider sx={{ my: 1.5 }} />

                {[
                  { label: 'Saldo anterior', value: fmt(recibo.saldoAnterior) },
                  { label: 'Saldo actual', value: fmt(recibo.saldoNovo), color: 'primary.main', bold: true },
                ].map(({ label, value, color, bold }) => (
                  <Stack key={label} direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
                    <Typography variant="body2" color="text.secondary">{label}</Typography>
                    <Typography variant="body2" fontWeight={bold ? 700 : 400} color={color || 'text.primary'}>
                      {value}
                    </Typography>
                  </Stack>
                ))}

                <Divider sx={{ my: 1.5 }} />

                {/* Agent + timestamp */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
                  <Typography variant="caption" color="text.secondary">Agente</Typography>
                  <Typography variant="caption" fontWeight={600} fontFamily="monospace">
                    {recibo.agente.nome} · {recibo.agente.codigo}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
                  <Typography variant="caption" color="text.secondary">Data/hora</Typography>
                  <Typography variant="caption">{fmtDt(recibo.dtTransacao)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center" py={0.5}>
                  <Typography variant="caption" color="text.secondary">ID transacção</Typography>
                  <Typography variant="caption" fontFamily="monospace">
                    {recibo.transacaoId.substring(0, 8).toUpperCase()}
                  </Typography>
                </Stack>

                {/* QR placeholder */}
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    textAlign: 'center',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Typography variant="caption" color="text.disabled">
                    QR Code do recibo
                  </Typography>
                  <Typography variant="h4" fontFamily="monospace" color="text.disabled" display="block">
                    ▤
                  </Typography>
                  <Typography variant="caption" color="text.disabled" fontFamily="monospace">
                    {recibo.transacaoId.substring(0, 8).toUpperCase()}
                  </Typography>
                </Box>
              </Box>

              {/* Footer actions */}
              <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<Print />}
                  onClick={handlePrint}
                  sx={{ flex: 1, borderRadius: 2 }}
                >
                  Imprimir
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setRecibo(null)}
                  sx={{ flex: 1, borderRadius: 2, fontWeight: 700 }}
                >
                  Fechar
                </Button>
              </Box>
            </DialogContent>
          </Box>
        )}
      </Dialog>

      {/* Print styles */}
      <style>{`
        @media print {
          body > *:not([data-print-target]) { display: none !important; }
          .MuiDialog-root { position: static !important; }
          .MuiBackdrop-root { display: none !important; }
          .MuiDialog-paper { box-shadow: none !important; max-width: 100% !important; }
        }
      `}</style>
    </Box>
  );
}
